/* The intake module's React root (designs/client-framework.md CF6), cut to
   ONE form and ONE queue by designs/intake-unified-form.md UF8. The module
   bar is the whole nav: About intake · Queue · + New effort · Groom (admin) ·
   Reports ▾ · Manage teams (admin) · identity. There is no domain tab strip,
   no wizard, no per-domain About — a requester files in plain words and the
   platform managers route the effort when they groom it. */
import { useCallback, useEffect, useState, type JSX, type ReactNode } from "react";
import { flushSync } from "react-dom";
import { createRoot, type Root } from "react-dom/client";
import type { CapabilityBoardRow } from "../../../shared/capabilities";
import type { CanonicalTeam } from "../../../shared/canonical-teams";
import type { Product, Vehicle } from "../../../shared/products";
import { getBoard, getMe, getProducts, getTeams, getVehicles, type Me } from "./api";
import { AboutIntake } from "./AboutIntake";
import { AuditLog } from "./AuditLog";
import { Board } from "./Board";
import { CaptureForm } from "./CaptureForm";
import { toView } from "./board-view";
import { NavBtn, NavDropdown } from "./nav";
import { Rollup } from "./Rollup";

/* every view the module bar can show. The queue is the landing view (UF8):
   a reader sees what the org is working on, and "+ New effort" is one click. */
type View = "queue" | "form" | "about" | "rollup" | "audit";

export function IntakeApp(): JSX.Element {
  const [view, setView] = useState<View>("queue");
  const [notice, setNotice] = useState<string | null>(null);

  /* identity, the team vocabulary and the ONE board — loaded once here, so
     every view reads the same rows. Each read lands independently (PR-92
     review): a transient board failure must not blank identity or teams,
     and the first failure names itself. */
  const [me, setMe] = useState<Me | null>(null);
  const [teams, setTeams] = useState<CanonicalTeam[] | null>(null);
  const [rows, setRows] = useState<CapabilityBoardRow[] | null>(null);
  /* the product list (UF21) — a missing list degrades to ids on the queue,
     never blocks it, so it is not in the failure set below */
  const [products, setProducts] = useState<Product[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback((): void => {
    setError(null);
    void getProducts()
      .then(setProducts)
      .catch(() => setProducts([]));
    void getVehicles()
      .then(setVehicles)
      .catch(() => setVehicles([]));
    void Promise.allSettled([getBoard(), getTeams(), getMe()]).then(([board, teamList, who]) => {
      if (board.status === "fulfilled") setRows(board.value);
      if (teamList.status === "fulfilled") setTeams(teamList.value);
      if (who.status === "fulfilled") setMe(who.value);
      const failed = [board, teamList, who].find(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      if (failed)
        setError(failed.reason instanceof Error ? failed.reason.message : String(failed.reason));
    });
  }, []);
  useEffect(load, [load]);

  /* "View as submitter" (UP11): a CLIENT-ONLY preview, and an identity fact,
     so it lives beside the identity chip. */
  const [previewing, setPreviewing] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem("intake-preview-submitter") === "1";
    } catch {
      return false;
    }
  });
  const setPreview = (on: boolean): void => {
    setPreviewing(on);
    try {
      sessionStorage.setItem("intake-preview-submitter", on ? "1" : "0");
    } catch {
      /* private mode etc. — the toggle still works for this page */
    }
  };
  const realAdmin = me?.roles.includes("admin") ?? false;
  /* a stored preview only means something for a resolved admin (PR-99
     review): force it off rather than show a banner claiming a role the
     server never granted */
  useEffect(() => {
    if (me !== null && !realAdmin && previewing) setPreview(false);
  }, [me, realAdmin, previewing]);
  const isAdmin = realAdmin && !previewing;
  /* the module bar renders from `realAdmin` — the identity chip must keep
     naming the real role while previewing, since it is the control that
     turns the preview back off — and from `isAdmin` for the admin views.
     What the bar CONTAINS follows the previewed role: the queue's stats and
     drawer read roles from `me`, so they get the subtracted view. */
  const shownMe =
    me !== null && previewing ? { ...me, roles: me.roles.filter((r) => r !== "admin") } : me;

  const go = (v: View): void => {
    setNotice(null);
    setView(v);
  };
  const backToQueue = (): void => {
    setView("queue");
    load();
  };

  const moduleBar = (
    <header className="topbar intake-modulebar" data-intake-modulebar>
      <nav className="topnav">
        {/* AB6: the ONE story, first in the bar — it explains everything
            after it — and open to everyone, like the queue it precedes */}
        <NavBtn label="About intake" active={view === "about"} onClick={() => go("about")} />
        {/* NOT admin-gated (MD12-e): intake exists to show the org what it
            has asked for. Grooming, which is an edit, stays Admin and is
            enforced at the server. */}
        <NavBtn label="Queue" active={view === "queue"} onClick={() => go("queue")} />
        {/* the bar's one call to action: this is where input happens */}
        <NavBtn label="+ New effort" active={view === "form"} primary onClick={() => go("form")} />
        {/* no Groom tab: the board is the queue's Board view (intake-groom.md IG11) */}
        <NavDropdown
          label="Reports"
          active={view === "rollup" || view === "audit"}
          items={[
            { label: "Exec rollup", active: view === "rollup", onClick: () => go("rollup") },
            { label: "Audit log", active: view === "audit", onClick: () => go("audit") },
          ]}
        />
        {/* team curation moved to the sidebar's Teams section, Admin only
            (teams-and-products.md TP8) */}
      </nav>
      <div className="spacer" />
      {realAdmin ? (
        <div className="nav-menu whoami-menu">
          <NavDropdown
            /* realAdmin ⇒ roles answered ⇒ a signed-in user exists */
            label={`${me!.user!.name} · ${previewing ? "previewing submitter" : "Admin"}`}
            active={previewing}
            items={[
              { label: "Admin view", active: !previewing, onClick: () => setPreview(false) },
              { label: "View as submitter", active: previewing, onClick: () => setPreview(true) },
            ]}
          />
        </div>
      ) : (
        <span className="whoami" title="Signed in — every team submits.">
          {me?.user?.name ?? "—"}
        </span>
      )}
    </header>
  );

  const back = (
    <button className="capture-notdup capture-back" onClick={backToQueue}>
      ← Back to the queue
    </button>
  );

  let body: ReactNode;
  if (view === "about")
    body = (
      <div data-intake-module="about">
        {back}
        <AboutIntake onStart={() => go("form")} />
      </div>
    );
  else if (view === "audit")
    body = (
      <div data-intake-module="audit">
        {back}
        <AuditLog teams={teams} />
      </div>
    );
  else if (view === "form")
    /* reachable regardless of the board read (PR-92 review): the form loads
       the team list itself, so a slow or failed board never blocks an ask */
    body = (
      <div data-intake-module="form">
        {back}
        <CaptureForm
          onAbout={() => go("about")}
          onRecorded={(cap) => {
            setNotice(`Registered “${cap.name}”. It sits under “Nobody owns this yet” until a platform manager grooms it.`);
            setView("queue");
            load();
          }}
        />
      </div>
    );
  else if (rows === null || teams === null)
    body =
      error !== null ? (
        <p className="capture-error">{error}</p>
      ) : (
        <p className="capture-loading">Loading the queue…</p>
      );
  else if (view === "rollup")
    body = (
      <div data-intake-module="rollup">
        {back}
        <Rollup rows={toView(rows, teams, products, vehicles)} />
      </div>
    );
  else
    body = (
      <Board
        rows={toView(rows, teams, products, vehicles)}
        teams={teams}
        products={products}
        me={shownMe}
        notice={notice}
        error={error}
        onNew={() => go("form")}
        onAbout={() => go("about")}
        onReload={load}
      />
    );

  return (
    <div className="intake">
      {moduleBar}
      {previewing && realAdmin && (
        <div className="preview-banner" role="status">
          <span>
            <b>Previewing the submitter view</b> — admin controls hidden. The server still holds
            your real role.
          </span>
          <button onClick={() => setPreview(false)}>Back to Admin view</button>
        </div>
      )}
      <div data-intake-panel>{body}</div>
    </div>
  );
}

/* The vanilla shell's view hook (CF6): render() calls this while the view
   is "intake". The root mounts once per visit and survives repaints; the
   commit is synchronous so no React work outlives a jsdom test file (the
   #77 flake's lesson). */
let intakeRoot: Root | null = null;

export function renderIntake(doc: Document): void {
  /* the module carries its own identity (topbar brand + module bar), so
     the shell's navigator heading and banner line would only repeat it —
     both cleared, the content starts where the masthead ends (per Lewis,
     Aug 31: no repeated Intake messages, no wasted band) */
  doc.getElementById("navigator")!.innerHTML = "";
  doc.getElementById("banner")!.innerHTML = "";
  doc.getElementById("footnote")!.innerHTML = "";
  const sections = doc.getElementById("sections")!;
  if (!sections.querySelector("#intake-root")) {
    sections.innerHTML = "";
    const host = doc.createElement("div");
    host.id = "intake-root";
    sections.appendChild(host);
    intakeRoot?.unmount();
    intakeRoot = createRoot(host);
  }
  flushSync(() => intakeRoot!.render(<IntakeApp />));
}
